import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { QueryKeys } from 'src/constants';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import ApiService from 'src/modules/api-service';
import {
  CreatePurchaseAgentRequest,
  PurchaseAgentListItem,
  PurchaseAgentState,
  UpdatePurchaseAgentRequest,
} from 'src/modules/generated/api';

export const usePurchaseAgents = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery(
    [QueryKeys.purchaseAgents],
    () => ApiService.purchaseAgent.purchaseAgentControllerList().then((res) => res.data),
    { enabled },
  );

export const usePurchaseAgentDetail = (id?: string) =>
  useQuery(
    [QueryKeys.purchaseAgentDetail, id],
    () => ApiService.purchaseAgent.purchaseAgentControllerDetail(id!).then((res) => res.data),
    { enabled: !!id },
  );

export const useCreatePurchaseAgent = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  return useMutation(
    (newPurchaseAgent: CreatePurchaseAgentRequest) =>
      ApiService.purchaseAgent.purchaseAgentControllerCreate(newPurchaseAgent),
    {
      onSuccess: () => {
        snackbar.showSuccess(t('purchaseAgent.messages.createSuccess'));
      },
      onError: (error) => {
        console.error('[usePurchaseAgents] createPurchaseAgent failed', error);
        snackbar.showError(t('purchaseAgent.messages.createError'));
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
      },
    },
  );
};

export const useDeletePurchaseAgent = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  const mutation = useMutation(
    (purchaseAgentId: string) => ApiService.purchaseAgent.purchaseAgentControllerDelete(purchaseAgentId),
    {
      onMutate: async (targetId: string) => {
        await queryClient.cancelQueries([QueryKeys.purchaseAgents]);

        const snapshot = queryClient.getQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents]);

        queryClient.setQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents], (prev = []) =>
          prev.filter((pa) => pa.id?.purchaseAgentId !== targetId),
        );

        return snapshot;
      },
      onError: (error, _targetId, snapshot) => {
        console.error('[usePurchaseAgents] deletePurchaseAgent failed', error);
        queryClient.setQueryData<PurchaseAgentListItem[] | undefined>([QueryKeys.purchaseAgents], snapshot);
        snackbar.showError(t('purchaseAgent.delete.error'));
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
      },
    },
  );

  return mutation;
};

export const useUpdatePurchaseAgent = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  return useMutation(
    ({ id, body }: { id: string; body: UpdatePurchaseAgentRequest }) =>
      ApiService.purchaseAgent.purchaseAgentControllerUpdate(id, body),
    {
      onSuccess: () => {
        snackbar.showSuccess(t('purchaseAgent.messages.updateSuccess'));
      },
      onError: (error) => {
        console.error('[usePurchaseAgents] updatePurchaseAgent failed', error);
        snackbar.showError(t('purchaseAgent.messages.updateError'));
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
        queryClient.invalidateQueries([QueryKeys.purchaseAgentDetail]);
      },
    },
  );
};

export const useTogglePurchaseAgentActive = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  return useMutation(
    ({ id, currentState }: { id: string; currentState: PurchaseAgentState }) =>
      currentState === PurchaseAgentState.Activated
        ? ApiService.purchaseAgent.purchaseAgentControllerDeactivate(id)
        : ApiService.purchaseAgent.purchaseAgentControllerActivate(id),
    {
      onMutate: async ({ id, currentState }) => {
        await queryClient.cancelQueries([QueryKeys.purchaseAgents]);
        const snapshot = queryClient.getQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents]);
        queryClient.setQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents], (prev = []) =>
          prev.map((pa) =>
            pa.id?.purchaseAgentId === id
              ? {
                  ...pa,
                  state:
                    currentState === PurchaseAgentState.Activated
                      ? PurchaseAgentState.Deactivated
                      : PurchaseAgentState.Activated,
                }
              : pa,
          ),
        );
        return snapshot;
      },
      onError: (error, vars, snapshot) => {
        console.error('[usePurchaseAgents] togglePurchaseAgentActive failed', error);
        queryClient.setQueryData<PurchaseAgentListItem[] | undefined>([QueryKeys.purchaseAgents], snapshot);

        const isActivating = vars.currentState !== PurchaseAgentState.Activated;
        const errorCode = axios.isAxiosError<{ errorCode?: string }>(error)
          ? error.response?.data?.errorCode
          : undefined;

        const key =
          isActivating && errorCode === 'SEARCH_AGENT_DEACTIVATED'
            ? 'purchaseAgent.activate.errorCodes.search_agent_deactivated'
            : isActivating
              ? 'purchaseAgent.activate.error'
              : 'purchaseAgent.deactivate.error';
        snackbar.showError(t(key));
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
      },
    },
  );
};
